import { ArrowRight, GraduationCap, PlayCircle } from "lucide-react"
import { Link } from "react-router-dom"
import { useCourses } from "../../apis/queries"
import type { Course } from "../../types"
import SvgCourseThumbnail from "../Course/SvgCourseThumbnail"
import { Container, Img, LinkButton, accentFor, cn } from "../ui"

export interface HeroStat {
  value: string
  label: string
}

export interface HeroProps {
  /**
   * Rendered only when supplied. The previous hero hardcoded "50K+ students",
   * "500+ courses" and "230 online now"; nothing here invents numbers, so pass
   * real figures once there's a source for them.
   */
  stats?: HeroStat[]
}

const RISE = "animate-fade-up motion-reduce:animate-none"

/**
 * One real course, as a compact row: thumbnail, category, title.
 *
 * Not `CourseCard` — that one carries pricing, a cart control and an instructor
 * row, none of which belong in a hero. An earlier version stacked three full
 * cards at offset heights, which looked deliberate in the abstract and looked
 * broken in the browser: unequal heights, an orphaned caption, and covers that
 * had not loaded. Uniform rows cannot fall out of alignment.
 */
const CourseRow = ({ course, priority }: { course: Course; priority: boolean }) => {
  const palette = accentFor(course.categories?.[0])

  return (
    <Link
      to={`/course-details/${course.slug || course._id}`}
      className={cn(
        "group flex items-stretch gap-4 overflow-hidden rounded-lg bg-background-card p-3",
        "ring-1 ring-white/10 transition-shadow duration-200 hover:ring-2 hover:ring-gold",
      )}
    >
      {/* Explicit height, not `h-full`: the row is a flex container with no
          definite height, so a percentage height on the child resolved to auto
          and the cover rendered as a strip over its own tint. */}
      {/* 120x80 is 3:2, the ratio the covers are drawn at; w-28 (112px) was
          1.4:1 and shaved the sides off each one. */}
      <div className={cn("relative h-20 w-[120px] shrink-0 overflow-hidden rounded-md", palette.soft)}>
        <Img
          src={course.coverImage}
          width={120}
          height={80}
          priority={priority}
          className="h-full w-full"
          fallback={<SvgCourseThumbnail title={course.title} width={112} height={80} seed={course._id} />}
        />
      </div>

      <div className="flex min-w-0 flex-col justify-center pr-1">
        {course.categories?.[0] && (
          <span className={cn("w-fit rounded-sm border px-1.5 py-0.5 text-overline font-semibold uppercase", palette.chip)}>
            {course.categories[0]}
          </span>
        )}
        <p className="mt-1.5 line-clamp-2 font-sans text-caption font-semibold text-text-primary">
          {course.title}
        </p>
      </div>
    </Link>
  )
}

/**
 * The hero.
 *
 * Two decisions carry it. First, it sits on deep ink while the rest of the page
 * stays off-white — the site opens on a confident dark plate and then lightens,
 * which is the one place the logo's blue and gold can both run at full strength.
 * Second, the artwork is the product: three real published courses, pulled from
 * the same query `PopularCourses` already makes (React Query dedupes it, so this
 * costs no extra request). A visitor sees actual courses in the first screen
 * rather than an abstract graphic — and nothing here can misrepresent the
 * catalogue, because it *is* the catalogue.
 *
 * If the API returns nothing the whole right-hand column is simply omitted and
 * the headline takes the full width. No placeholder, no invented course.
 */
const Hero = ({ stats }: HeroProps) => {
  const { data, isLoading } = useCourses({ featured: true })
  const showcase = (data?.courses ?? []).slice(0, 3)

  // The panel is committed as soon as the request is in flight, and it renders
  // whatever comes back rather than demanding exactly three. Requiring three
  // meant a slow or failing API silently swapped the hero for an entirely
  // different centred composition — the page appeared to redesign itself
  // depending on the network.
  const showPanel = isLoading || showcase.length > 0

  return (
    <section className="relative overflow-hidden bg-surface-ink text-white">
      <Container className="relative py-16 sm:py-20 lg:py-24">
        <div className={cn("grid gap-16", showPanel && "lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-20")}>
          <div className={cn(!showPanel && "mx-auto max-w-3xl text-center")}>
            <p className={cn("flex items-center gap-2.5 text-overline font-semibold uppercase tracking-[0.08em] text-gold", RISE, !showPanel && "justify-center")}>
              <GraduationCap size={16} aria-hidden="true" />
              Mindalitix Academy
            </p>

            {/* Deliberately larger than the display scale's top step: the headline
                is the loudest thing on the site and everything else defers to it. */}
            <h1
              className={cn("mt-6 font-display font-semibold text-white", RISE)}
              style={{ animationDelay: "60ms", fontSize: "clamp(2.75rem, 7vw, 5rem)", lineHeight: 0.98, letterSpacing: "-0.03em" }}
            >
              Learn the skills that{" "}
              <span className="text-gold">move you forward</span>.
            </h1>

            <p
              className={cn("mt-7 max-w-xl text-body-lg text-white/70", RISE, !showPanel && "mx-auto")}
              style={{ animationDelay: "120ms" }}
            >
              Structured courses, hands-on practice and certificates you can show for it —
              taught by people who work in the field.
            </p>

            <div
              className={cn("mt-10 flex flex-col gap-3 sm:flex-row sm:items-center", RISE, !showPanel && "sm:justify-center")}
              style={{ animationDelay: "180ms" }}
            >
              <LinkButton to="/courses" size="lg" variant="gold" trailingIcon={<ArrowRight size={18} aria-hidden="true" />}>
                Browse courses
              </LinkButton>
              <LinkButton to="/about-us" size="lg" variant="inverse" leadingIcon={<PlayCircle size={18} aria-hidden="true" />}>
                How it works
              </LinkButton>
            </div>
          </div>

          {showPanel && (
            <div className={cn("hidden sm:block", RISE)} style={{ animationDelay: "240ms" }}>
              <p className="mb-4 flex items-center justify-between gap-4 text-overline font-semibold uppercase tracking-[0.08em] text-white/50">
                Featured right now
                <Link to="/courses" className="text-gold underline-offset-4 hover:underline">
                  All courses
                </Link>
              </p>
              <div className="grid gap-3">
                {isLoading
                  ? // Same footprint as a real row, so nothing shifts when data lands.
                    [0, 1, 2].map((i) => (
                      <div key={i} className="flex gap-4 rounded-lg bg-white/[0.06] p-3 ring-1 ring-white/10">
                        <div className="h-20 w-28 shrink-0 animate-pulse rounded-md bg-white/10" />
                        <div className="flex flex-1 flex-col justify-center gap-2">
                          <div className="h-3 w-16 animate-pulse rounded-sm bg-white/10" />
                          <div className="h-3 w-4/5 animate-pulse rounded-sm bg-white/10" />
                          <div className="h-3 w-3/5 animate-pulse rounded-sm bg-white/10" />
                        </div>
                      </div>
                    ))
                  : showcase.map((course, index) => (
                      <CourseRow key={course._id} course={course} priority={index === 0} />
                    ))}
              </div>
              <p className="mt-5 border-l-2 border-gold pl-4 text-caption text-white/60">
                Every course is built around practice, not just video.
              </p>
            </div>
          )}
        </div>

        {stats && stats.length > 0 && (
          <dl
            className={cn("mt-20 grid grid-cols-2 gap-x-8 gap-y-10 border-t border-white/15 pt-10 sm:grid-cols-4", RISE)}
            style={{ animationDelay: "300ms" }}
          >
            {stats.map((stat) => (
              <div key={stat.label}>
                <dd className="font-display text-h1 tabular-nums text-gold">{stat.value}</dd>
                <dt className="mt-1 text-overline font-semibold uppercase tracking-[0.08em] text-white/60">
                  {stat.label}
                </dt>
              </div>
            ))}
          </dl>
        )}
      </Container>

      {/* The four logo accents, closing the plate and handing off to the light page. */}
      <div aria-hidden="true" className="flex h-1.5">
        <span className="flex-1 bg-accent-orange" />
        <span className="flex-1 bg-accent-gold" />
        <span className="flex-1 bg-accent-cyan" />
        <span className="flex-1 bg-accent-blue" />
      </div>
    </section>
  )
}

export default Hero
